/* eslint-disable vue/no-unused-properties */

import {
  RNCameraActionPayload,
  RNCameraActionReturnPayload,
  RNPayload,
} from '@/helpers/types';
import {
  DateTimeFormat,
  FacilityServiceShortCodes,
  OrderProblemType,
  ProblemCodes,
  ProblemReportTypes,
  RNPayloadActions,
  RouteNames,
  StatusBarStyles,
  StoreMutations,
} from '@/helpers/enums';
import {
  captureExceptionToSentry,
  getMobileAppData,
  getMobileInsetsData,
  sendErrorToSentry,
  userData,
} from '@/helpers/utils';
import {
  FacilityOrder,
  FacilityOrderBagItem,
  Order,
} from '@/providers/laundryheap/interfaces/facility-orders';
import { defineComponent } from 'vue';
import { LocationAsRelativeRaw, RouteParamsRaw } from 'vue-router';
import { ServiceItem } from '@/providers/laundryheap/interfaces/services';
import { AddSpecialItemToFacilityOrderBag } from '@/providers/laundryheap/types';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import isToday from 'dayjs/plugin/isToday';
import {
  bulkyServices,
  ironingServices,
  washServices,
} from '@/helpers/constants';

import { measureBlur } from '@/helpers/measureBlur';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isToday);

// Preload all images in the images directory so we can check if image exists
const assetImages: Record<string, string> = import.meta.glob(
  '/src/images/**/*',
  {
    eager: true,
    import: 'default',
  }
);

export default defineComponent({
  computed: {
    phonePrefix(): string {
      return userData.data?.facility.serviceCountry.phonePrefix || '+44';
    },
    weightUnit(): string {
      return userData.data?.facility.serviceCountry.weightUnit || 'kg';
    },
    currency(): string {
      return userData.data?.facility.serviceCountry.currency || '£';
    },
    maxPriceLength(): number {
      return userData.data?.facility.serviceCountry.stationPriceLength || 3;
    },
    previousRoute(): string | null {
      return this.$store.state.previousRoute;
    },
  },
  methods: {
    async checkIfPhotoIsBlurry(image: string) {
      const percentageThreshold = 0.6; // 60% threshold
      try {
        // Higher score means a sharper image (lower = more blurry)
        const score = await measureBlur(image);
        return score <= percentageThreshold;
      } catch (e) {
        sendErrorToSentry('Measure blur failed', {
          message: (e as Error).message,
        });
        // In case if measure blur fails, we treat image as good quality image
        return false;
      }
    },
    imgUrl(path: string): string {
      const imagePath = `/src/images/${path}`;
      const imageExists = assetImages[imagePath];

      if (imageExists) {
        return imageExists;
      }

      throw new Error(`Image not found: ${path}`);
    },
    setPreviousRoute(name: RouteNames, params?: RouteParamsRaw): void {
      const data: LocationAsRelativeRaw = { name };
      if (params) {
        data.params = params;
      }
      this.$store.commit(
        StoreMutations.setPreviousRoute,
        this.$router.resolve(data).fullPath
      );
    },
    async defaultGoBack(): Promise<void> {
      // If we have stored previous route go to that path (webView is not storing history) otherwise go home
      if (this.previousRoute) {
        await this.$router
          .push({ path: this.previousRoute })
          .catch(async (e): Promise<void> => {
            captureExceptionToSentry(e);
            await this.routerGoBack();
          });
        return;
      }

      await this.routerGoBack();
    },
    async routerGoBack(): Promise<void> {
      if (window.history.length > 1) {
        await this.$router.back();
      } else {
        // Fallback if you opened app on subpage
        await this.$router.push({ name: RouteNames.orders });
      }
    },
    validEmail(email: string): boolean {
      const re =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      return re.test(String(email).toLowerCase());
    },
    validPhone(number: string): boolean {
      const re = /^[+][0-9]{3}?[0-9]{7,9}$/;
      return re.test(number.replace(' ', ''));
    },
    capitalizeFirstLetter(string: string): string {
      return string.charAt(0).toUpperCase() + string.slice(1);
    },
    itemCountAndUnit(
      item:
        | FacilityOrderBagItem
        | ServiceItem
        | AddSpecialItemToFacilityOrderBag
    ): string | number {
      if (!item.qty && !item.weight) {
        return '';
      }

      return item.qty || `${item.weight} ${this.weightUnit}`;
    },
    openQrScanner(data?: Record<string, unknown>): void {
      const payload = {
        action: RNPayloadActions.qr_scan,
        data: data,
      };

      this.messageReactNative(payload);
    },
    updateStatusBarStyle(style: StatusBarStyles): void {
      const payload = {
        action: RNPayloadActions.set_status_bar_style,
        data: { statusBarStyle: style },
      };

      this.messageReactNative(payload);
    },
    openCamera(data?: RNCameraActionPayload): Promise<void> {
      const payload = {
        action: RNPayloadActions.camera,
        data: data,
      };

      this.messageReactNative(payload);

      return new Promise((resolve) => {
        // Wait for camera to slide in with the animation
        // RN has 350ms animation duration
        setTimeout(resolve, 450);
      });
    },
    /**
     * Camera V2, now using promises which resolve with the image, allowing easier photo taking.
     */
    openCameraV2(
      data: RNCameraActionPayload
    ): Promise<RNCameraActionReturnPayload> {
      const requestKey = this.shortID();

      return new Promise((resolve, _reject) => {
        const handler = (payload: RNCameraActionReturnPayload): void => {
          this.$store.commit('removeCameraRequest', requestKey);
          resolve(payload);
        };

        this.$store.commit('createCameraRequest', {
          requestKey,
          handler,
        });

        this.messageReactNative({
          action: RNPayloadActions.camera,
          data: { ...data, requestKey },
        });
      });
    },
    messageReactNative(payload: RNPayload): void {
      window.ReactNativeWebView.postMessage(JSON.stringify(payload));
    },
    safeAreaStyles(): Record<string, string> {
      const insets = getMobileInsetsData();

      const top = insets?.top && insets.top >= 0 ? Math.ceil(insets.top) : 30;
      const bottom =
        insets?.bottom && insets.bottom >= 0 ? Math.ceil(insets.bottom) : 10;
      const left =
        insets?.left && insets.left >= 0 ? Math.ceil(insets.left) : 0;
      const right =
        insets?.right && insets.right >= 0 ? Math.ceil(insets.right) : 0;
      return {
        'padding-top': `${top}px`,
        'padding-bottom': `${bottom}px`,
        'padding-left': `${left}px`,
        'padding-right': `${right}px`,
      };
    },
    getMonth(monthIndex: number): string {
      const monthNames = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
      ];

      return monthNames[monthIndex] || 'undefined';
    },
    serviceIcon(
      serviceShortCode: FacilityServiceShortCodes,
      warning: boolean = false
    ): string {
      try {
        if (warning) {
          return this.imgUrl(`services/${serviceShortCode}_warning.svg`);
        }

        return this.imgUrl(`services/${serviceShortCode}.svg`);
      } catch (e) {
        try {
          return this.imgUrl(`services/${serviceShortCode}.svg`);
        } catch (e) {
          return this.imgUrl(`services/fallback.svg`);
        }
      }
    },
    isObjectEmpty(object: object): boolean {
      if (!object) {
        return true;
      }
      return Object.keys(object).length === 0;
    },
    isArrayEmpty(array: Array<unknown>): boolean {
      return array.length < 1;
    },
    /**
     * Returns `array` without `value`. Uses simple comparison.
     * @param array
     * @param value
     */
    removeValueFromArray<T>(array: Array<T>, value: T): Array<T> {
      return array.filter((val) => val !== value);
    },
    isMobileApp(): boolean {
      const mobileData = getMobileAppData();
      return !!mobileData && !this.isObjectEmpty(mobileData);
    },
    getBagProblemIcon(
      shortCode: ProblemCodes,
      problemType: ProblemReportTypes,
      light = false
    ): string | null {
      if (light) {
        shortCode = `${shortCode}_light` as ProblemCodes;
      }
      try {
        return this.imgUrl(
          `icons/bag_problems/${problemType}/${shortCode}.svg`
        );
      } catch {
        sendErrorToSentry('getBagProblemIcon', {
          message: `icons/bag_problems/${problemType}/${shortCode}.svg`,
        });
        return null;
      }
    },
    getItemProblemIcon(
      shortCode: ProblemCodes | undefined,
      problemType: ProblemReportTypes | null | undefined,
      light = false
    ): string | null {
      if (light) {
        shortCode = `${shortCode}_light` as ProblemCodes;
      }
      try {
        return this.imgUrl(
          `icons/item_problems/${problemType}/${shortCode}.svg`
        );
      } catch {
        sendErrorToSentry('getItemProblemIcon', {
          message: `icons/item_problems/${problemType}/${shortCode}.svg`,
        });
        return null;
      }
    },
    getProblemName(shortCode: ProblemCodes, type: ProblemReportTypes): string {
      return this.$t(`problems.names.${type}.${shortCode}`);
    },
    getBagProblemName(
      shortCode: ProblemCodes,
      type: ProblemReportTypes
    ): string {
      return this.$t(`problems.names.bag.${type}.${shortCode}`);
    },
    /**
     * Returns values that appear in both arrays. Warning: Values can be duplicated.
     * @param array1
     * @param array2
     */
    arrayIntersection<T>(array1: T[], array2: T[]): T[] {
      return array1.filter((value) => array2.includes(value));
    },
    /**
     * Returns values that are unique in each array.
     * @param array1
     * @param array2
     */
    arrayDifference<T>(array1: T[], array2: T[]): T[] {
      const set1 = this.uniqueValues(array1);
      const set2 = this.uniqueValues(array2);
      const values = [] as T[];
      for (const value of set1) {
        if (!set2.includes(value)) {
          values.push(value);
        }
      }
      for (const value of set2) {
        if (!set1.includes(value)) {
          values.push(value);
        }
      }
      return values;
    },
    uniqueValues<T>(array: T[]): T[] {
      const uniqueValueSet = new Set();
      return array.filter((element) => {
        const isDuplicate = uniqueValueSet.has(element);
        uniqueValueSet.add(element);
        return !isDuplicate;
      });
    },
    arrayOfDeliveredPackages(facilityOrder: FacilityOrder | null): string[] {
      return (
        facilityOrder?.facilityOrderPackages
          .filter((pack) => !!pack.deliveredAt)
          .map((pack) => pack.qrCode) || []
      );
    },
    arrayOfUndeliveredPackages(facilityOrder: FacilityOrder | null): string[] {
      return (
        facilityOrder?.facilityOrderPackages
          .filter((pack) => !pack.deliveredAt)
          .map((pack) => pack.qrCode) || []
      );
    },
    facilityOrderProblemStateIcon(
      type: OrderProblemType | null
    ): string | undefined {
      try {
        switch (type) {
          /* Waiting for reply */
          case OrderProblemType.unresolved:
            return this.imgUrl('icons/support/problem_states/waiting.svg');
          /* Resolved confirmation status */
          case OrderProblemType.confirmed:
            return this.imgUrl('icons/support/problem_states/confirmed.svg');
          case OrderProblemType.unconfirmed:
            return this.imgUrl('icons/support/problem_states/unconfirmed.svg');
          /* Resolved status */
          case OrderProblemType.accepted:
            return this.imgUrl('icons/support/problem_states/accepted.svg');
          case OrderProblemType.rejected:
            return this.imgUrl('icons/support/problem_states/rejected.svg');
          default:
            return undefined;
        }
      } catch (e) {
        return undefined;
      }
    },
    /* Generate a short random alphanumeric id. Example: 4ksw3g */
    shortID(): string {
      const firstPart = (Math.random() * 46656) | 0;
      const secondPart = (Math.random() * 46656) | 0;
      const firstPartString = ('000' + firstPart.toString(36)).slice(-3);
      const secondPartString = ('000' + secondPart.toString(36)).slice(-3);
      return firstPartString + secondPartString;
    },
    /**
     * Returns `true` if shift is less than 1 hour in the future (or in the past)
     * @param readyBy
     */
    shiftIsUpcoming(readyBy: string): boolean {
      if (!readyBy) {
        return false;
      }

      const currentTime = new Date().getTime();
      const shiftTime = Date.parse(readyBy);

      /* If date is invalid, return false */
      if (!shiftTime) {
        return false;
      }

      return shiftTime - currentTime < 60 * 60 * 1000;
    },
    /* Date-time functions */
    /**
     * Creates a `dayjs` instance from order. Also applies timezone from order.
     * @param order
     * @return `dayjs` instance or `null` if `order?.readyByTime` is falsy
     */
    getReadyByDateObject(order: Order): dayjs.Dayjs | null {
      if (!order.readyByTime) {
        return null;
      }
      const timezone = order.timezone || undefined;
      return dayjs.tz(order.readyByTime, timezone);
    },
    isReadyByToday(order: Order): boolean {
      return this.getReadyByDateObject(order)?.isToday() || false;
    },
    /**
     * Takes `readyBy` from order and adds `processingTime` days.
     * @param order
     * @param processingTime Number of days
     * @param format Format you want back. Defaults to `DateTimeFormat.long`
     * @return Date time label or `null`, if order has no `timezone` or `readyByTime`.
     */
    extraProcessingTimeLabel(
      order: Order,
      processingTime: string | number,
      format: DateTimeFormat = DateTimeFormat.long
    ): string | null {
      try {
        const processingTimeDays = Math.round(Number(processingTime)) || 0;
        const timeNow = dayjs().tz(order.timezone);

        return timeNow
          ?.add(processingTimeDays, 'day')
          ?.locale(this.$i18n.locale)
          .format(this.localizeDateTimeFormat(format));
      } catch (e) {
        sendErrorToSentry('extraProcessingTimeLabel', {
          readyByTime: order?.readyByTime,
          timezone: order?.timezone,
          format: format,
          processingTime: processingTime,
        });
        return null;
      }
    },
    supportCenterDateTimeLabel(dateTime: string, timezone?: string): string {
      /* If today, return only hh:mm, else Tue 12 Jan 2025, 13:42*/
      const dateObj = dayjs.tz(dateTime, timezone).locale(this.$i18n.locale);
      if (dateObj.isToday()) {
        return dateObj.format('HH:mm');
      }
      return dateObj.format('ddd, DD MMM YYYY, HH:mm');
    },
    /**
     * Localizes the literals in DateTimeFormat.long
     * @param format
     */
    localizeDateTimeFormat(format: DateTimeFormat): string {
      if (format !== DateTimeFormat.long) {
        return format;
      }

      const localizedAt = this.$t('localization.datetime_at');
      if (!localizedAt) {
        return format;
      }

      return format.replace('[at]', `[${localizedAt}]`);
    },
    /**
     * Formats date time as "Wednesday, 23 Nov at 11AM"
     * @param dateTime
     * @param timeZone
     * @param format
     */
    formatDateTime(
      dateTime: string | Date,
      timeZone?: string,
      format: DateTimeFormat = DateTimeFormat.long
    ): string | null {
      return dayjs
        .tz(dateTime, timeZone)
        .locale(this.$i18n.locale)
        .format(this.localizeDateTimeFormat(format));
    },
    /**
     * Returns `true` if `dateTime` hour is after provided `hour`, otherwise returns `false`. Uses 24 hour system.
     * @param dateTime
     * @param hour
     * @param timezone
     */
    isAfterHour(
      dateTime: string | Date,
      hour: number,
      timezone?: string
    ): boolean {
      const hourNow = dayjs(dateTime).tz(timezone).hour();
      return hourNow >= hour;
    },
    triggerFunctionsInSequence(functions: { (): void }[], delay: number): void {
      for (let i = 0; i < functions.length; i++) {
        setTimeout(functions[i], delay * (i + 1));
      }
    },
    cleanServiceShortcode(
      shortcode: FacilityServiceShortCodes
    ): FacilityServiceShortCodes {
      if (!shortcode) {
        return FacilityServiceShortCodes.unprocessed;
      }
      if (washServices.includes(shortcode)) {
        return FacilityServiceShortCodes.wash;
      }
      if (ironingServices.includes(shortcode)) {
        return FacilityServiceShortCodes.ironing;
      }
      if (bulkyServices.includes(shortcode)) {
        return FacilityServiceShortCodes.duvets_bulky_items;
      }
      return shortcode;
    },
  },
});
